import { msg } from "@lingui/core/macro"
import type { MessageDescriptor } from "@lingui/core"
import type { I18n } from "@lingui/core"
import type {
  DealDrawerActivityFacet,
  DealDrawerActivityItem,
} from "@/types/v2/conversation"

// Client-side facet filtering for the DEAL activity timeline
// (CON-146). Event items carry a `facet { field, toId,
// toLabel }`; the facet row renders one dropdown per distinct
// `facet.field` present among the loaded events, each listing
// that field's distinct `toLabel` values. Selecting a value
// narrows the feed to event rows whose `facet.field` +
// `facet.toLabel` match — every NON-event row (message / note
// / call) is left untouched, so a facet only ever constrains
// the update rows, never the conversation.
//
// All pure functions here so the facet row, the feed, and the
// tests share one source of truth for "which facets exist" and
// "does this item pass the active facet".

// Friendly labels for the facet pill, keyed by the server's
// `facet.field`. The deal model tracks stage / value / status
// / name today; `owner_id` is mapped ahead of owner-change
// tracking landing, so the moment the backend starts emitting
// an `owner_id` facet the "Owner" pill renders with no
// frontend change. Any unmapped field falls back to a
// title-cased version of the column so a new tracked field is
// never invisible.
export const FACET_FIELD_LABELS: Record<string, MessageDescriptor> = {
  stage_id: msg`Stage`,
  value: msg`Value`,
  status: msg`Status`,
  name: msg`Name`,
  owner_id: msg`Owner`,
}

export function facetFieldLabel(field: string, i18n: I18n): string {
  const known = FACET_FIELD_LABELS[field]
  if (known) return i18n._(known)
  // Fallback: "some_field" → "Some field". Strip a trailing
  // "_id" so an unmapped foreign key reads as the entity name.
  const base = field.replace(/_id$/, "")
  return base
    .split("_")
    .map((part, i) =>
      i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part,
    )
    .join(" ")
}

// One facet field present in the loaded events, plus the
// distinct labelled values selectable under it. `values` is
// ordered by first appearance in the items list so the
// dropdown reads in timeline order.
export interface FacetGroup {
  field: string
  label: string
  values: { toLabel: string }[]
}

// The active facet selection. `field` + `toLabel` together
// identify the chosen value; `null` means no facet is active
// (the feed shows every kind).
export interface ActiveFacet {
  field: string
  toLabel: string
}

// Type guard: an event item that actually carries a usable
// facet (present, non-null, with a field and a display label).
// `toLabel` is what both the dropdown shows and the filter
// matches on, so a facet without one can't participate.
function usableFacet(
  item: DealDrawerActivityItem,
): (DealDrawerActivityFacet & { toLabel: string }) | null {
  const facet = item.facet
  if (!facet) return null
  if (!facet.field) return null
  if (facet.toLabel == null || facet.toLabel === "") return null
  return facet as DealDrawerActivityFacet & { toLabel: string }
}

// Build the facet groups present among the loaded items.
// Distinct by `field`, each with its distinct `toLabel`
// values — both ordered by first appearance so the row and
// dropdowns read in the order events happened. Returns an
// empty array when no event carries a usable facet (so the
// caller renders nothing).
export function extractFacetGroups(
  items: DealDrawerActivityItem[],
  i18n: I18n,
): FacetGroup[] {
  const order: string[] = []
  const byField = new Map<string, Map<string, true>>()

  for (const item of items) {
    const facet = usableFacet(item)
    if (!facet) continue
    let values = byField.get(facet.field)
    if (!values) {
      values = new Map()
      byField.set(facet.field, values)
      order.push(facet.field)
    }
    values.set(facet.toLabel, true)
  }

  return order.map((field) => ({
    field,
    label: facetFieldLabel(field, i18n),
    values: Array.from(byField.get(field)!.keys()).map((toLabel) => ({
      toLabel,
    })),
  }))
}

// Narrow the items to the active facet. With no active facet,
// returns the list unchanged. With one active, every row that
// ISN'T an event with a facet passes through (messages, notes,
// calls, created/assignment events) — facets only constrain
// the rows that carry the SAME `field` as the active facet, of
// which only the matching `toLabel` survives.
export function filterByFacet(
  items: DealDrawerActivityItem[],
  active: ActiveFacet | null,
): DealDrawerActivityItem[] {
  if (!active) return items
  return items.filter((item) => {
    const facet = usableFacet(item)
    // Non-faceted rows, and faceted rows for a DIFFERENT field,
    // are never constrained by this facet.
    if (!facet || facet.field !== active.field) return true
    return facet.toLabel === active.toLabel
  })
}
